import { useEffect, useState, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { Search, Download, Loader2, X, ExternalLink, StickyNote, Users, UserPlus, Trash2, Eye, History } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["Yeni", "Arandı", "Eşleşti", "Kapandı", "Spam"];
const token = () => localStorage.getItem("he_admin_token");
const ROLE_LABELS = { full_admin: "Full Admin", agent: "Agent", viewer: "Görüntüleyen" };

function StatCard({ label, value }) {
  return (
    <div className="bg-paper border border-line rounded-lg p-5">
      <div className="text-xs uppercase tracking-[0.15em] text-stone">{label}</div>
      <div className="mt-2 font-serif text-4xl tracking-tight">{value}</div>
    </div>
  );
}

function Chart({ title, data }) {
  return (
    <div className="bg-paper border border-line rounded-lg p-5">
      <div className="text-xs uppercase tracking-[0.15em] text-stone mb-4">{title}</div>
      {data.length === 0 ? (
        <div className="h-40 grid place-items-center text-sm text-stone">Veri yok</div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12, fill: "#71717A" }} axisLine={false} tickLine={false} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {data.map((_, i) => <Cell key={i} fill="#0A0A0A" />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function AdminPanel({ user }) {
  const role = user?.role;
  const isFull = role === "full_admin";
  const canEdit = role === "full_admin" || role === "agent";
  const canExport = canEdit;

  const [stats, setStats] = useState(null);
  const [view, setView] = useState("leads");
  const [tab, setTab] = useState("owner");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", city: "", status: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const loadLeads = useCallback(() => {
    setLoading(true);
    const params = { type: tab };
    Object.entries(filters).forEach(([k, val]) => { if (val) params[k] = val; });
    api.get("/admin/leads", { params })
      .then((r) => setLeads(r.data.leads))
      .catch(() => toast.error("Yüklenemedi"))
      .finally(() => setLoading(false));
  }, [tab, filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadLeads(); }, [loadLeads]);

  // Fetch full detail on open so the view is recorded in the audit log
  const openLead = async (l) => {
    setSelected(l);
    try {
      const r = await api.get(`/admin/leads/${l.id}`);
      setSelected(r.data);
    } catch { /* keep row data */ }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}/status`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    loadStats();
  };

  const exportCsv = async () => {
    const params = new URLSearchParams({ type: tab });
    Object.entries(filters).forEach(([k, val]) => { if (val) params.append(k, val); });
    try {
      const res = await fetch(`${API}/admin/export?${params}`, { headers: { Authorization: `Bearer ${token()}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `leads-${tab}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Dışa aktarma başarısız"); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-8">
      {isFull && (
        <div className="mb-6 flex gap-1 p-1 bg-paper border border-line rounded-md w-fit">
          {[["leads", "Lead'ler"], ["users", "Kullanıcılar"]].map(([id, label]) => (
            <button key={id} onClick={() => setView(id)} data-testid={`admin-view-${id}`}
              className={`h-9 px-4 rounded text-sm font-medium transition-colors ${view === id ? "bg-ink text-paper" : "text-stone hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {isFull && view === "users" ? (
        <UsersManager currentUser={user} />
      ) : (
      <>
      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <StatCard label="Bugün" value={stats.today} />
            <StatCard label="Bu Hafta" value={stats.week} />
            <StatCard label="Bu Ay" value={stats.month} />
            <StatCard label="Toplam" value={stats.total} />
            <StatCard label="Ev Sahibi" value={stats.owners} />
            <StatCard label="Kiracı" value={stats.tenants} />
          </div>
          <div className="mt-3 grid gap-3 lg:grid-cols-3">
            <Chart title="Şehir Bazlı" data={stats.by_city} />
            <Chart title="Kaynak (UTM)" data={stats.by_source} />
            <Chart title="Durum" data={stats.by_status} />
          </div>
        </>
      )}

      {/* Tabs + controls */}
      <div className="mt-8 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-1 p-1 bg-paper border border-line rounded-md">
          {[["owner", "Ev Sahibi"], ["tenant", "Kiracı"], ["club", "Kulüp"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`h-9 px-4 rounded text-sm font-medium transition-colors ${tab === id ? "bg-ink text-paper" : "text-stone hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>
        {canExport && (
          <button onClick={exportCsv} data-testid="admin-export" className="flex items-center gap-2 h-9 px-4 rounded-md border border-line text-sm hover:border-ink transition-colors bg-paper">
            <Download size={15} /> CSV
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone" />
          <input placeholder="Ara…" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            data-testid="admin-search"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-line bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
        </div>
        <input placeholder="Şehir" value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
          className="h-10 px-3 rounded-md border border-line bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="h-10 px-3 rounded-md border border-line bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-ink">
          <option value="">Tüm durumlar</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
          className="h-10 px-3 rounded-md border border-line bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
          className="h-10 px-3 rounded-md border border-line bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
      </div>

      {/* Table */}
      <div className="mt-4 bg-paper border border-line rounded-lg overflow-hidden">
        {loading ? (
          <div className="h-40 grid place-items-center"><Loader2 className="animate-spin text-ink" /></div>
        ) : leads.length === 0 ? (
          <div className="h-40 grid place-items-center text-sm text-stone">Kayıt bulunamadı</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-stone">
                  <th className="px-4 py-3 font-medium">Tarih</th>
                  <th className="px-4 py-3 font-medium">İsim</th>
                  <th className="px-4 py-3 font-medium">İletişim</th>
                  {tab === "owner" && <><th className="px-4 py-3 font-medium">Adres</th><th className="px-4 py-3 font-medium">Kira</th></>}
                  {tab === "tenant" && <><th className="px-4 py-3 font-medium">Şehirler</th><th className="px-4 py-3 font-medium">Bütçe</th></>}
                  {tab === "club" && <><th className="px-4 py-3 font-medium">Şehir</th><th className="px-4 py-3 font-medium">Mülk Sahibi</th></>}
                  <th className="px-4 py-3 font-medium">Durum</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} onClick={() => openLead(l)} data-testid="admin-lead-row"
                    className="border-b border-line last:border-0 hover:bg-mist cursor-pointer transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-stone">{new Date(l.created_at).toLocaleDateString("tr-TR")}</td>
                    <td className="px-4 py-3 font-medium">{l.name}</td>
                    <td className="px-4 py-3 text-stone">{l.phone}</td>
                    {tab === "owner" && (
                      <>
                        <td className="px-4 py-3">
                          <a onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${l.address?.street} ${l.address?.huisnummer}, ${l.address?.city}`)}`}
                            className="inline-flex items-center gap-1 hover:underline">
                            {l.address?.street} {l.address?.huisnummer}, {l.address?.city}
                            <ExternalLink size={12} />
                          </a>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">€{l.rent}</td>
                      </>
                    )}
                    {tab === "tenant" && (
                      <>
                        <td className="px-4 py-3">{(l.cities || []).join(", ")}</td>
                        <td className="px-4 py-3 whitespace-nowrap">€{l.budget_min}–{l.budget_max}</td>
                      </>
                    )}
                    {tab === "club" && (
                      <>
                        <td className="px-4 py-3">{l.city || "—"}</td>
                        <td className="px-4 py-3">{l.owns_property || "—"}</td>
                      </>
                    )}
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} disabled={!canEdit}
                        data-testid="admin-status-select"
                        className="h-8 px-2 rounded border border-line bg-paper text-xs focus:outline-none focus:ring-1 focus:ring-ink disabled:opacity-60 disabled:cursor-not-allowed">
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <LeadDetail lead={selected} onClose={() => setSelected(null)} onStatus={updateStatus} onNoteAdded={loadLeads} canEdit={canEdit} isFull={isFull} />}
      </>
      )}
    </div>
  );
}

function LeadDetail({ lead, onClose, onStatus, onNoteAdded, canEdit, isFull }) {
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState(lead.notes || []);
  const [views, setViews] = useState([]);
  const isOwner = lead.type === "owner";
  const isClub = lead.type === "club";
  const addr = lead.address || {};

  useEffect(() => {
    setNotes(lead.notes || []);
    if (isFull) {
      api.get(`/admin/leads/${lead.id}/views`).then((r) => setViews(r.data.views || [])).catch(() => {});
    }
  }, [lead, isFull]);

  const addNote = async () => {
    if (!note.trim()) return;
    const r = await api.post(`/admin/leads/${lead.id}/notes`, { text: note });
    setNotes((n) => [...n, r.data.note]);
    setNote("");
    onNoteAdded?.();
  };

  const rows = isOwner
    ? [
        ["Mülk tipi", lead.property_type], ["Oda", lead.rooms], ["m²", lead.sqm],
        ["Eşya", lead.furnishing], ["Kira", `€${lead.rent}`], ["Müsait", lead.available_date],
        ["Inschrijving", lead.inschrijving], ["Açıklama", lead.description],
      ]
    : isClub
    ? [
        ["Şehir", lead.city], ["Mülk sahibi", lead.owns_property],
        ["İlgi alanı", lead.interest], ["Mesaj", lead.message],
      ]
    : [
        ["Şehirler", (lead.cities || []).join(", ")], ["Mülk tipi", lead.property_type],
        ["Min oda", lead.min_rooms], ["Bütçe", `€${lead.budget_min}–${lead.budget_max}`],
        ["Taşınma", lead.move_date], ["Kalış", lead.stay_duration], ["Kişi", lead.occupants],
        ["Çalışma", lead.work_status], ["Gelir", lead.income_range], ["Evcil", lead.pets],
        ["Sigara", lead.smoking], ["Inschrijving gerekli", lead.inschrijving_needed],
      ];

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="admin-lead-detail">
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-paper w-full sm:max-w-2xl max-h-[90vh] rounded-t-xl sm:rounded-xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <div>
            <div className="font-medium text-lg">{lead.name}</div>
            <div className="text-xs text-stone">{new Date(lead.created_at).toLocaleString("tr-TR")}</div>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 hover:opacity-60"><X size={22} /></button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <a href={`tel:${lead.phone}`} className="rounded-md border border-line p-3 hover:border-ink transition-colors"><div className="text-xs text-stone">Telefon</div>{lead.phone}</a>
            <a href={`mailto:${lead.email}`} className="rounded-md border border-line p-3 hover:border-ink transition-colors truncate"><div className="text-xs text-stone">E-posta</div>{lead.email}</a>
            <div className="rounded-md border border-line p-3"><div className="text-xs text-stone">Kanal</div>{lead.preferred_channel}</div>
          </div>

          {isOwner && (
            <div>
              <div className="text-xs uppercase tracking-wider text-stone mb-1">Adres</div>
              <a target="_blank" rel="noopener noreferrer"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${addr.street} ${addr.huisnummer}, ${addr.postcode} ${addr.city}`)}`}
                className="inline-flex items-center gap-1 font-medium hover:underline">
                {addr.street} {addr.huisnummer}{addr.toevoeging}, {addr.postcode} {addr.city} <ExternalLink size={13} />
              </a>
              {addr.lat && addr.lon && (
                <iframe title="map" className="mt-3 w-full h-48 rounded-md border border-line"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${addr.lon - 0.004}%2C${addr.lat - 0.002}%2C${addr.lon + 0.004}%2C${addr.lat + 0.002}&layer=mapnik&marker=${addr.lat}%2C${addr.lon}`} />
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {rows.filter(([, val]) => val !== undefined && val !== null && val !== "").map(([k, val]) => (
              <div key={k} className="flex justify-between border-b border-line py-1.5">
                <span className="text-stone">{k}</span><span className="font-medium text-right">{val}</span>
              </div>
            ))}
          </div>

          {isOwner && (lead.photos || []).length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-stone mb-2">Fotoğraflar</div>
              <div className="flex flex-wrap gap-2">
                {lead.photos.map((id) => (
                  <a key={id} href={`${API}/files/${id}`} target="_blank" rel="noopener noreferrer">
                    <img src={`${API}/files/${id}`} alt="" className="h-24 w-24 object-cover rounded-md border border-line" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone mb-2"><StickyNote size={14} /> İç Notlar</div>
            <div className="space-y-2 mb-3">
              {notes.length === 0 && <div className="text-sm text-stone">Henüz not yok.</div>}
              {notes.map((n, i) => (
                <div key={i} className="text-sm bg-mist rounded-md p-3">
                  {n.text}<div className="text-xs text-stone mt-1">{n.by ? `${n.by} · ` : ""}{new Date(n.at).toLocaleString("tr-TR")}</div>
                </div>
              ))}
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not ekle…" data-testid="admin-note-input"
                  onKeyDown={(e) => e.key === "Enter" && addNote()}
                  className="flex-1 h-10 px-3 rounded-md border border-line text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
                <button onClick={addNote} data-testid="admin-note-add" className="h-10 px-4 rounded-md bg-ink text-paper text-sm">Ekle</button>
              </div>
            )}
          </div>

          {isFull && (
            <div data-testid="admin-view-history">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone mb-2"><History size={14} /> Görüntüleme Geçmişi</div>
              {views.length === 0 ? (
                <div className="text-sm text-stone">Henüz görüntüleme kaydı yok.</div>
              ) : (
                <>
                  <div className="text-sm mb-2">
                    Son gören: <span className="font-medium">{views[0].user_name || views[0].user_email}</span>
                    <span className="text-xs text-stone"> · {new Date(views[0].at).toLocaleString("tr-TR")}</span>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1.5">
                    {views.map((v, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-mist rounded-md px-3 py-2">
                        <span className="flex items-center gap-2">
                          <Eye size={13} className="text-stone" />
                          {v.user_name || v.user_email}
                          <span className="px-1.5 py-0.5 rounded bg-brand/10 text-brand text-[10px] font-semibold uppercase">{ROLE_LABELS[v.user_role] || v.user_role}</span>
                        </span>
                        <span className="text-xs text-stone">{new Date(v.at).toLocaleString("tr-TR")}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-line flex items-center gap-2">
          <span className="text-sm text-stone">Durum:</span>
          <select value={lead.status} onChange={(e) => onStatus(lead.id, e.target.value)} disabled={!canEdit}
            className="h-9 px-3 rounded-md border border-line text-sm focus:outline-none focus:ring-2 focus:ring-ink disabled:opacity-60 disabled:cursor-not-allowed">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

function UsersManager({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "agent" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => toast.error("Yüklenemedi")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Kullanıcı eklendi");
      setForm({ email: "", name: "", password: "", role: "agent" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (email, role) => {
    try {
      await api.patch(`/admin/users/${encodeURIComponent(email)}/role`, { role });
      setUsers((us) => us.map((u) => (u.email === email ? { ...u, role } : u)));
      toast.success("Rol güncellendi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncellenemedi");
    }
  };

  const removeUser = async (email) => {
    if (!window.confirm(`${email} silinsin mi?`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`);
      setUsers((us) => us.filter((u) => u.email !== email));
      toast.success("Kullanıcı silindi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-manager">
      {/* Add user */}
      <div className="bg-paper border border-line rounded-lg p-5">
        <div className="flex items-center gap-2 text-sm font-semibold mb-4"><UserPlus size={16} /> Yeni Kullanıcı Ekle</div>
        <form onSubmit={addUser} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 items-end">
          <div className="lg:col-span-1">
            <label className="block text-xs text-stone mb-1">Ad Soyad</label>
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name"
              className="w-full h-10 px-3 rounded-md border border-line text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
          </div>
          <div>
            <label className="block text-xs text-stone mb-1">E-posta</label>
            <input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email"
              className="w-full h-10 px-3 rounded-md border border-line text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
          </div>
          <div>
            <label className="block text-xs text-stone mb-1">Şifre</label>
            <input required type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password"
              placeholder="min 6 karakter"
              className="w-full h-10 px-3 rounded-md border border-line text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
          </div>
          <div>
            <label className="block text-xs text-stone mb-1">Rol</label>
            <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} data-testid="user-role"
              className="w-full h-10 px-3 rounded-md border border-line text-sm focus:outline-none focus:ring-2 focus:ring-ink">
              <option value="full_admin">Full Admin</option>
              <option value="agent">Agent</option>
              <option value="viewer">Görüntüleyen</option>
            </select>
          </div>
          <button type="submit" disabled={saving} data-testid="user-add-btn"
            className="h-10 px-5 rounded-md bg-brand text-white text-sm font-semibold hover:bg-brandhover transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 size={15} className="animate-spin" />} Ekle
          </button>
        </form>
        <p className="mt-3 text-xs text-stone">Not: Şifreyi siz belirlersiniz ve kullanıcıya iletirsiniz.</p>
      </div>

      {/* Users list */}
      <div className="bg-paper border border-line rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 text-sm font-semibold px-5 py-4 border-b border-line"><Users size={16} /> Kullanıcılar ({users.length})</div>
        {loading ? (
          <div className="h-40 grid place-items-center"><Loader2 className="animate-spin text-ink" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-stone">
                  <th className="px-4 py-3 font-medium">Ad Soyad</th>
                  <th className="px-4 py-3 font-medium">E-posta</th>
                  <th className="px-4 py-3 font-medium">Rol</th>
                  <th className="px-4 py-3 font-medium text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.email === currentUser?.email;
                  return (
                    <tr key={u.email} className="border-b border-line last:border-0" data-testid="user-row">
                      <td className="px-4 py-3 font-medium">{u.name} {isSelf && <span className="text-xs text-stone">(siz)</span>}</td>
                      <td className="px-4 py-3 text-stone">{u.email}</td>
                      <td className="px-4 py-3">
                        <select value={u.role} onChange={(e) => changeRole(u.email, e.target.value)} disabled={isSelf}
                          data-testid="user-role-select"
                          className="h-8 px-2 rounded border border-line bg-paper text-xs focus:outline-none focus:ring-1 focus:ring-ink disabled:opacity-60 disabled:cursor-not-allowed">
                          <option value="full_admin">Full Admin</option>
                          <option value="agent">Agent</option>
                          <option value="viewer">Görüntüleyen</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!isSelf && (
                          <button onClick={() => removeUser(u.email)} data-testid="user-delete-btn"
                            className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 transition-colors">
                            <Trash2 size={14} /> Sil
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
